import { useQuery, UseQueryResult, useQueryClient, UseMutationResult, useMutation } from '@tanstack/react-query';

import {
  ImportResourceResponse,
  ImportSession,
  ImportSessionData,
  ImportSessionQueryData,
  ImportSessionResource,
  ImportSessionResourceResponse,
  ImportSessionStatusCode
} from '../@types';
import { createImportSession, getImportSessionById, getImportSessionSummary, getImportedResourceBySessionId, updateImportSession, uploadResource } from '../api/imports';
import { ImportSessionKey, ImportSessionResourceKey, ImportSessionSummaryKey } from '../configs/imports';

export const useCreateImportSession = (type: string): UseMutationResult<ImportSession, Error, ImportSessionData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ImportSessionData) => {
      return await createImportSession(data);
    },

    onSuccess: async (response: ImportSession) => {
      await queryClient.invalidateQueries({
        queryKey: [ImportSessionKey, type, response.id]
      });
    }
  });
};

export const useGetImportSessionById = (type: string, sessionId: number, enabled: boolean): UseQueryResult<ImportSession> => {
  return useQuery({
    queryKey: [ImportSessionKey, type, sessionId],
    queryFn: () => getImportSessionById(sessionId, type),
    refetchInterval: 2000,
    enabled: enabled
  });
};

export const useGetImportedResourceBySessionId = (type: string, sessionId: number, enabled: boolean, queryData: ImportSessionQueryData): UseQueryResult<ImportSessionResourceResponse> => {
  return useQuery({
    queryKey: [ImportSessionResourceKey, type, sessionId],
    queryFn: () => getImportedResourceBySessionId(sessionId, type, queryData),
    refetchInterval: 2000,
    enabled: enabled
  });
};

export const useGetImportSessionSummary = (type: string, sessionId: number, enabled: boolean): UseQueryResult<ImportSessionResource[]> => {
  return useQuery({
    queryKey: [ImportSessionSummaryKey, type, sessionId],
    queryFn: () => getImportSessionSummary(sessionId, type),
    refetchInterval: 2000,
    enabled: enabled
  });
};

export interface UploadResourceData {
  file: File;
  type: string;
}

export const useUploadResource = (): UseMutationResult<ImportResourceResponse, Error, UploadResourceData> => {
  return useMutation({
    mutationFn: async (data: UploadResourceData) => {
      const formData = new FormData();
      const files = data.file;
      const type = data.type;
      formData.append('resourceImports', files);
      return await uploadResource(type, formData);
    }
  });
};

export interface UpdateImportSessionData extends ImportSessionStatusCode {
  id: number;
}

export const useUpdateImportSession = (type: string): UseMutationResult<ImportSession, Error, UpdateImportSessionData> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateImportSessionData) => {
      return await updateImportSession(data.id, data, type);
    },

    onSuccess: async (response: ImportSession) => {
      await queryClient.invalidateQueries({
        queryKey: [ImportSessionKey, type, response.id]
      });
    }
  });
};
